const WIDGET_API = 'https://api.judge.me/api/v1/widgets/all_reviews_page';
const LOADED_EVENT = 'topeak:reviews-2-loaded';
const MARQUEE_SPEED_FACTOR = 45;
const MARQUEE_RESIZE_DEBOUNCE_MS = 250;

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {number} rating
 * @returns {string}
 */
function renderStars(rating) {
  const score = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  return `${'★'.repeat(score)}${'☆'.repeat(5 - score)}`;
}

/**
 * @typedef {Object} ParsedReview
 * @property {number} rating
 * @property {string} title
 * @property {string} body
 * @property {string} author
 * @property {number} createdAt
 */

/**
 * @param {string} html
 * @returns {ParsedReview[]}
 */
function parseReviewsFromWidgetHtml(html) {
  const container = document.createElement('div');
  container.innerHTML = html;

  const reviews = [];

  for (const element of container.querySelectorAll('.jdgm-rev')) {
    const rating = Number.parseInt(
      element.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '0',
      10
    );
    const title =
      element.querySelector('.jdgm-rev__title')?.textContent.trim() ||
      element.getAttribute('data-product-title')?.trim() ||
      '';
    const body = element.querySelector('.jdgm-rev__body')?.textContent.trim() || '';
    const author = element.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Verified customer';
    const timestamp = element.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') || '';
    const createdAt = Date.parse(timestamp) || 0;

    if (!body && !title) continue;

    reviews.push({
      rating: rating || 5,
      title,
      body,
      author,
      createdAt,
    });
  }

  return reviews.sort((left, right) => right.createdAt - left.createdAt);
}

/**
 * @param {ParsedReview} review
 * @returns {string}
 */
function renderReviewCard(review) {
  const rating = review.rating || 5;
  const starsLabel = `${rating} out of 5 stars`;
  const title = review.title?.trim() || '';
  const body = review.body?.trim() || '';
  const author = review.author?.trim() || 'Verified customer';

  const titleMarkup = title
    ? `<p class="topeak-reviews-2__title">${escapeHtml(title)}</p>`
    : '';
  const bodyMarkup = body
    ? `<blockquote class="topeak-reviews-2__quote"><p>${escapeHtml(body)}</p></blockquote>`
    : '';
  const authorMarkup = `<cite class="topeak-reviews-2__author">— ${escapeHtml(author)}</cite>`;

  return `
    <li class="topeak-reviews-2__card" role="listitem">
      <div class="topeak-reviews-2__stars" aria-label="${escapeHtml(starsLabel)}">
        <span aria-hidden="true">${renderStars(rating)}</span>
      </div>
      ${titleMarkup}
      ${bodyMarkup}
      ${authorMarkup}
    </li>
  `;
}

/**
 * @param {HTMLElement} list
 * @returns {HTMLElement[]}
 */
function getOriginalCards(list) {
  return [...list.querySelectorAll(':scope > .topeak-reviews-2__card:not([data-marquee-clone])')];
}

/**
 * @param {HTMLElement} list
 * @param {HTMLElement[]} originals
 * @returns {number}
 */
function measureLoopDistance(list, originals) {
  if (originals.length === 0) return 0;

  const firstClone = list.querySelector(':scope > .topeak-reviews-2__card[data-marquee-clone]');
  if (!(firstClone instanceof HTMLElement)) return 0;

  const firstOriginal = originals[0];
  const distance = firstClone.offsetLeft - firstOriginal.offsetLeft;

  return distance > 0 ? distance : 0;
}

/**
 * @param {() => void} callback
 * @param {number} delayMs
 * @returns {() => void}
 */
function debounce(callback, delayMs) {
  /** @type {number | undefined} */
  let timeoutId;

  return () => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }

    timeoutId = window.setTimeout(() => {
      timeoutId = undefined;
      callback();
    }, delayMs);
  };
}

class TopeakReviews2Grid extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #marqueeResizeObserver = null;

  /** @type {MediaQueryList | null} */
  #reducedMotionMedia = null;

  /** @type {(() => void) | null} */
  #reducedMotionHandler = null;

  /** @type {(() => void) | null} */
  #debouncedMarqueeResize = null;

  /** @type {{ viewportWidth: number; setsNeeded: number } | null} */
  #marqueeLayout = null;

  connectedCallback() {
    this.#reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotionHandler = () => {
      this.#syncMarqueeState();
    };
    this.#reducedMotionMedia.addEventListener('change', this.#reducedMotionHandler);
    this.#debouncedMarqueeResize = debounce(() => {
      this.#handleMarqueeResize();
    }, MARQUEE_RESIZE_DEBOUNCE_MS);
    this.#loadReviews();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#marqueeResizeObserver?.disconnect();
    this.#marqueeResizeObserver = null;
    this.#reducedMotionMedia?.removeEventListener('change', this.#reducedMotionHandler);
    this.#reducedMotionMedia = null;
    this.#reducedMotionHandler = null;
    this.#debouncedMarqueeResize = null;
    this.#marqueeLayout = null;
  }

  async #loadReviews() {
    const list = this.querySelector('.topeak-reviews-2__grid');
    if (!(list instanceof HTMLElement)) return;

    const shopDomain = this.dataset.shopDomain?.trim();
    const apiToken = this.dataset.apiToken?.trim();
    const reviewsCount = Math.min(10, Math.max(1, Number(this.dataset.reviewsCount) || 10));

    if (!shopDomain || !apiToken) {
      list.replaceChildren();
      list.removeAttribute('aria-busy');
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    list.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(WIDGET_API);
      url.searchParams.set('shop_domain', shopDomain);
      url.searchParams.set('api_token', apiToken);
      url.searchParams.set('per_page', String(reviewsCount));
      url.searchParams.set('page', '1');
      url.searchParams.set('review_type', 'product-reviews');

      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Judge.me request failed (${response.status})`);
      }

      const payload = await response.json();
      const widgetHtml =
        typeof payload.all_reviews === 'string'
          ? payload.all_reviews
          : typeof payload.widget === 'string'
            ? payload.widget
            : '';
      const reviews = parseReviewsFromWidgetHtml(widgetHtml).slice(0, reviewsCount);

      if (!reviews.length) {
        this.#showDesignNotice(
          list,
          'No published product reviews returned from Judge.me. Publish reviews in the Judge.me app and refresh.'
        );
        return;
      }

      list.innerHTML = reviews.map((review) => renderReviewCard(review)).join('');
      list.removeAttribute('aria-busy');
      await this.#setupMarquee(list);
      this.dispatchEvent(new CustomEvent(LOADED_EVENT, { bubbles: true }));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#showDesignNotice(
        list,
        'Could not load Judge.me reviews. Check the public API token, confirm reviews are published, and redeploy the theme.'
      );
    }
  }

  /**
   * @param {HTMLElement} list
   * @param {boolean} [allowRetry]
   */
  async #setupMarquee(list, allowRetry = true) {
    const runner = list.closest('.topeak-reviews-2__runner');
    const marquee = list.closest('.topeak-reviews-2__marquee');
    if (!(runner instanceof HTMLElement) || !(marquee instanceof HTMLElement)) return;

    this.#clearMarquee(list, runner);

    const originals = getOriginalCards(list);
    if (originals.length === 0) return;

    if (this.#reducedMotionMedia?.matches) {
      runner.classList.add('topeak-reviews-2__runner--static');
      return;
    }

    const viewportWidth = marquee.clientWidth;
    const firstOriginal = originals[0];
    const lastOriginal = originals[originals.length - 1];
    const setWidth = lastOriginal.offsetLeft + lastOriginal.offsetWidth - firstOriginal.offsetLeft;

    if (setWidth <= 0) {
      if (!allowRetry) return;

      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
      return this.#setupMarquee(list, false);
    }

    const setsNeeded = Math.max(2, Math.ceil((viewportWidth * 2) / setWidth));
    this.#appendCloneSets(list, originals, setsNeeded);
    this.#marqueeLayout = { viewportWidth, setsNeeded };

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    const loopDistance = measureLoopDistance(list, originals);
    if (loopDistance <= 0) return;

    runner.style.setProperty('--topeak-reviews-2-marquee-distance', `${loopDistance}px`);
    runner.style.setProperty(
      '--topeak-reviews-2-marquee-duration',
      `${Math.max(loopDistance / MARQUEE_SPEED_FACTOR, 12)}s`
    );
    runner.classList.add('topeak-reviews-2__runner--active');

    this.#marqueeResizeObserver?.disconnect();
    this.#marqueeResizeObserver = new ResizeObserver(() => {
      this.#debouncedMarqueeResize?.();
    });
    this.#marqueeResizeObserver.observe(marquee);
  }

  /**
   * @param {HTMLElement} list
   * @param {HTMLElement[]} originals
   * @param {number} setsNeeded
   */
  #appendCloneSets(list, originals, setsNeeded) {
    for (let setIndex = 1; setIndex < setsNeeded; setIndex += 1) {
      for (const card of originals) {
        const clone = card.cloneNode(true);
        if (!(clone instanceof HTMLElement)) continue;

        clone.setAttribute('data-marquee-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        list.append(clone);
      }
    }
  }

  #handleMarqueeResize() {
    const list = this.querySelector('.topeak-reviews-2__grid');
    const runner = this.querySelector('.topeak-reviews-2__runner');
    const marquee = this.querySelector('.topeak-reviews-2__marquee');

    if (!(list instanceof HTMLElement) || !(runner instanceof HTMLElement) || !(marquee instanceof HTMLElement)) {
      return;
    }

    const originals = getOriginalCards(list);
    if (originals.length === 0 || this.#reducedMotionMedia?.matches) return;

    const viewportWidth = marquee.clientWidth;
    const firstOriginal = originals[0];
    const lastOriginal = originals[originals.length - 1];
    const setWidth = lastOriginal.offsetLeft + lastOriginal.offsetWidth - firstOriginal.offsetLeft;

    if (setWidth <= 0) return;

    const setsNeeded = Math.max(2, Math.ceil((viewportWidth * 2) / setWidth));
    const previousLayout = this.#marqueeLayout;

    if (!previousLayout || previousLayout.setsNeeded !== setsNeeded) {
      this.#setupMarquee(list);
      return;
    }

    const loopDistance = measureLoopDistance(list, originals);
    if (loopDistance <= 0) return;

    runner.style.setProperty('--topeak-reviews-2-marquee-distance', `${loopDistance}px`);
    runner.style.setProperty(
      '--topeak-reviews-2-marquee-duration',
      `${Math.max(loopDistance / MARQUEE_SPEED_FACTOR, 12)}s`
    );
    this.#marqueeLayout = { viewportWidth, setsNeeded };
  }

  /**
   * @param {HTMLElement} list
   * @param {HTMLElement} runner
   */
  #clearMarquee(list, runner) {
    for (const clone of list.querySelectorAll('[data-marquee-clone]')) {
      clone.remove();
    }

    runner.classList.remove('topeak-reviews-2__runner--active', 'topeak-reviews-2__runner--static');
    runner.style.removeProperty('--topeak-reviews-2-marquee-distance');
    runner.style.removeProperty('--topeak-reviews-2-marquee-duration');
    this.#marqueeLayout = null;
  }

  #syncMarqueeState() {
    const list = this.querySelector('.topeak-reviews-2__grid');
    if (list instanceof HTMLElement && getOriginalCards(list).length > 0) {
      this.#setupMarquee(list);
    }
  }

  /**
   * @param {HTMLElement} list
   * @param {string} message
   */
  #showDesignNotice(list, message) {
    const runner = list.closest('.topeak-reviews-2__runner');

    this.#marqueeResizeObserver?.disconnect();
    this.#marqueeResizeObserver = null;
    this.#marqueeLayout = null;

    if (runner instanceof HTMLElement) {
      this.#clearMarquee(list, runner);
    }

    list.replaceChildren();
    list.removeAttribute('aria-busy');

    if (this.dataset.designMode !== 'true') return;

    const notice = document.createElement('p');
    notice.className = 'topeak-reviews-2__notice';
    notice.textContent = message;
    list.append(notice);
  }
}

if (!customElements.get('topeak-reviews-2-grid')) {
  customElements.define('topeak-reviews-2-grid', TopeakReviews2Grid);
}
